import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Settings, User, PhoneForwarded, Cog, ChevronRight, Lock, MessageSquare, Info } from "lucide-react";
import { cn } from "@/lib/utils";

export interface PhoneNumber {
  id: string;
  number: string;
  friendlyName: string;
  type: "local" | "toll-free";
  capabilities: {
    voice: boolean;
    sms: boolean;
    mms: boolean;
  };
  assignedTo?: {
    id: string;
    name: string;
  };
  isDefault: boolean;
  monthlyPrice: number;
  status: "active" | "pending" | "suspended";
  smsEnabled?: boolean;
}

export interface TeamMember {
  id: string;
  name: string;
  email: string;
}

interface ConfigureNumberModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  phoneNumber: PhoneNumber | null;
  teamMembers: TeamMember[];
  onSave: (config: {
    friendlyName: string;
    assignedTo?: string;
    callRecording: boolean;
    recordingMessage?: string;
    smsEnabled?: boolean;
  }) => void;
}

export function ConfigureNumberModal({
  open,
  onOpenChange,
  phoneNumber,
  teamMembers,
  onSave,
}: ConfigureNumberModalProps) {
  const [activeTab, setActiveTab] = useState("basic");
  const [friendlyName, setFriendlyName] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [callRecording, setCallRecording] = useState(false);
  const [recordingMessage, setRecordingMessage] = useState(
    "This call may be recorded for quality assurance purposes."
  );
  const [smsEnabled, setSmsEnabled] = useState(false);

  // Reset form when phone number changes
  useEffect(() => {
    if (phoneNumber) {
      setFriendlyName(phoneNumber.friendlyName || "");
      setAssignedTo(phoneNumber.assignedTo?.id || "");
      setSmsEnabled(phoneNumber.smsEnabled || false);
      setCallRecording(false);
      setActiveTab("basic");
    }
  }, [phoneNumber]);

  const handleSave = () => {
    onSave({
      friendlyName,
      assignedTo: assignedTo || undefined,
      callRecording,
      recordingMessage: callRecording ? recordingMessage : undefined,
      smsEnabled,
    });
    onOpenChange(false);
  };

  const handleClose = () => {
    // Reset form to phone number values
    setFriendlyName(phoneNumber?.friendlyName || "");
    setAssignedTo(phoneNumber?.assignedTo?.id || "");
    setSmsEnabled(phoneNumber?.smsEnabled || false);
    setCallRecording(false);
    setActiveTab("basic");
    onOpenChange(false);
  };

  if (!phoneNumber) return null;

  const hasSmsCapability = phoneNumber.capabilities?.sms ?? false;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10">
              <Settings className="h-5 w-5 text-primary" />
            </div>
            <div>
              <DialogTitle>Configure {phoneNumber.number}</DialogTitle>
              <DialogDescription>
                Configure your number preferences
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="basic">Basic Details</TabsTrigger>
            <TabsTrigger value="forwarding" className="relative">
              Call Forwarding
              <Badge
                variant="secondary"
                className="absolute -top-2 -right-2 text-[10px] px-1 py-0"
              >
                Soon
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="advanced" className="relative">
              Advanced
              <Badge
                variant="secondary"
                className="absolute -top-2 -right-2 text-[10px] px-1 py-0"
              >
                Soon
              </Badge>
            </TabsTrigger>
          </TabsList>

          {/* Basic Details Tab */}
          <TabsContent value="basic" className="space-y-6 pt-4">
            {/* Friendly Name */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="friendly-name">Friendly Name</Label>
                <span className="text-xs text-muted-foreground">
                  {friendlyName.length}/100
                </span>
              </div>
              <Input
                id="friendly-name"
                placeholder="e.g., Main Office Line"
                value={friendlyName}
                onChange={(e) => setFriendlyName(e.target.value.slice(0, 100))}
              />
              <p className="text-xs text-muted-foreground">
                A descriptive name to help identify this number
              </p>
            </div>

            {/* SMS Marketing Toggle */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <Label>Enable for SMS Marketing</Label>
                    <MessageSquare className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Allow this number to be used in SMS Marketing campaigns
                  </p>
                </div>
                <Switch
                  checked={smsEnabled}
                  onCheckedChange={setSmsEnabled}
                  disabled={!hasSmsCapability}
                />
              </div>

              {!hasSmsCapability && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900">
                  <Info className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800 dark:text-amber-200">
                    This number does not have SMS capability. SMS-enabled numbers can be purchased with SMS features.
                  </p>
                </div>
              )}

              {smsEnabled && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900">
                  <MessageSquare className="h-4 w-4 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-green-800 dark:text-green-200">
                    This number will appear in the "From Number" dropdown when sending SMS campaigns from the Marketing page.
                  </p>
                </div>
              )}
            </div>

            {/* Calls Go To — not yet implemented */}
            <div className="space-y-3 opacity-50 pointer-events-none">
              <div className="flex items-center gap-2">
                <Label>Calls Go To</Label>
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Coming Soon</Badge>
              </div>
              <div className="p-3 rounded-lg border bg-muted/50">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-background border">
                    <User className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">Team Member</p>
                    <p className="text-xs text-muted-foreground">
                      Route calls to a specific team member
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Call Recording — not yet implemented */}
            <div className="space-y-3 opacity-50 pointer-events-none">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <Label>Call Recording</Label>
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Coming Soon</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Record incoming and outgoing calls
                  </p>
                </div>
                <Switch checked={false} disabled />
              </div>
            </div>
          </TabsContent>

          {/* Call Forwarding Tab - Coming Soon */}
          <TabsContent value="forwarding" className="pt-4">
            <div className="text-center py-12">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-muted mx-auto mb-4">
                <PhoneForwarded className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium mb-2">Call Forwarding</h3>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto mb-4">
                Configure advanced call forwarding rules including sequential ringing,
                simultaneous ring, and time-based routing.
              </p>
              <Badge variant="secondary" className="gap-1">
                <Lock className="h-3 w-3" />
                Coming Soon
              </Badge>
            </div>
          </TabsContent>

          {/* Advanced Settings Tab - Coming Soon */}
          <TabsContent value="advanced" className="pt-4">
            <div className="text-center py-12">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-muted mx-auto mb-4">
                <Cog className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium mb-2">Advanced Settings</h3>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto mb-4">
                Configure advanced options like caller ID, voicemail settings,
                webhook integrations, and API access.
              </p>
              <Badge variant="secondary" className="gap-1">
                <Lock className="h-3 w-3" />
                Coming Soon
              </Badge>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
